import './chart.style.less'

import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2'
import {
  barOption,
  columnAndLineOption,
  doughnutOption,
  guageOption,
  hundredPercentStackedBarOption,
  hundredPercentStackedColumnOption,
  lineOption,
  pieOption,
  twoColumnOption,
  areaOption,
  columnOption,
  hundredPercentStackedBarGlassonsOption,
} from './MultiChart.option'
import { capitalize, get, upperCase } from 'lodash'
import {
  transformHundredPercentStakeBarChart,
  transformHundredPercentStakeBarGlassonsChart,
  transformTwoColumnChart,
} from '@/screens/Liveboard/Liveboard.util'
import {
  transformMultiAreaChart,
  transformMultiBarChart,
  transformMultiColumnAndLineChart,
  transformMultiColumnChart,
  transformMultiGaugeChart,
  transformMultiGaugeTargetChart,
  transformMultiHundredPercentStackedColumnChart,
  transformMultiLineChart,
  transformMultiLineTimeChart,
  transformMultiPieChart,
  transformTableChart,
} from './MultiChart.transform'

import GaugeTargetChart from './GaugeTarget'
import MainWrapper from './MainWrapper'
import { Menu } from 'antd'
import { useState, useLayoutEffect } from 'react'
import TableChart from './Table'

interface props {
  title: string
  sign?: string
  showLegend?: boolean
  data: any
  chartTypeData: string[] // real chartTypeData chatjs contains [string,......]
  id: string
  periods?: string[]
  item?: any
  target?: boolean
  onPeriodChange?: (period: string) => any
  datePicked?: any[]
  currentPeriod?: string
}

function MultiChart(props: props) {
  const {
    data,
    title,
    sign = '',
    chartTypeData,
    id,
    onPeriodChange,
    periods,
    item = {},
    target = false,
    showLegend = true,
    datePicked,
    currentPeriod,
  } = props

  const [targetNumber, settargetNumber] = useState<number>(null)
  const [plugins, setPlugins] = useState<any>()

  useLayoutEffect(() => {
    const plugins = [
      {
        id: 'liveboard',
        beforeInit: function (chart) {
          // Get reference to the original fit function
          const originalFit = chart.legend.fit
          // Override the fit function
          chart.legend.fit = function fit() {
            // Call original function and bind scope in order to use `this` correctly inside it
            originalFit.bind(chart.legend)()
            // const width = chart.width
            this.height += chart.width <= 700 ? 10 : 20
          }
        },
      },
    ]

    setPlugins(plugins)
  }, [])

  const [chartType, setChartType] = useState(get(chartTypeData, '[0]'))

  const menus = chartTypeData.map((item: any) => {
    return (
      <Menu.Item onClick={() => setChartType(item)} key={item}>
        {capitalize(upperCase(item))}
      </Menu.Item>
    )
  })
  const menu = <Menu activeKey={chartType}>{menus}</Menu>

  const renderChart = (type) => {
    switch (type) {
      case 'line_time_scale':
        const lineTimeData = transformMultiLineTimeChart(
          {
            ...data,
          },
          item
        )
        return (
          <Line
            redraw
            data={lineTimeData}
            options={lineOption(
              showLegend,
              sign,
              targetNumber,
              lineTimeData?.labels
            )}
            plugins={plugins}
          />
        )
      case 'line':
        const lineData = transformMultiLineChart(
          {
            ...data,
          },
          item,
          datePicked,
          currentPeriod
        )
        return (
          <Line
            redraw
            data={lineData}
            options={lineOption(
              showLegend,
              sign,
              targetNumber,
              lineData?.labels
            )}
            plugins={plugins}
          />
        )
      case 'area':
        const areaData = transformMultiAreaChart(data, item)
        return (
          <Line
            redraw
            data={areaData}
            options={areaOption(
              showLegend,
              sign,
              targetNumber,
              areaData?.labels
            )}
            plugins={plugins}
          />
        )
      case 'column':
        const columnData = transformMultiColumnChart(data, item)
        return (
          <Bar
            redraw
            data={columnData}
            options={columnOption(showLegend, sign, columnData?.labels)}
            plugins={plugins}
          />
        )
      case 'hundred_percent_stacked_column':
        return (
          <Bar
            redraw
            data={transformMultiHundredPercentStackedColumnChart(data, item)}
            options={hundredPercentStackedColumnOption(showLegend, sign)}
            plugins={plugins}
          />
        )
      case 'hundred_percent_stacked_bar':
        return (
          <Bar
            redraw
            data={transformHundredPercentStakeBarChart(data, item)}
            options={hundredPercentStackedBarOption(
              showLegend,
              sign,
              targetNumber
            )}
            plugins={plugins}
          />
        )
      case 'stacked_bar':
        return (
          <Bar
            redraw
            data={transformHundredPercentStakeBarGlassonsChart(data, item)}
            options={hundredPercentStackedBarGlassonsOption(
              showLegend,
              sign,
              targetNumber
            )}
            plugins={plugins}
          />
        )
      case 'bar':
        const barData = transformMultiBarChart(data, item)
        return (
          <Bar
            redraw
            data={barData}
            options={barOption(showLegend, sign, barData?.labels)}
            plugins={plugins}
          />
        )

      case 'pie':
        return (
          <Pie
            data={transformMultiPieChart(data, item)}
            options={pieOption(showLegend, sign)}
            plugins={plugins}
          />
        )

      case 'column_and_line':
        return (
          <Line
            redraw
            data={transformMultiColumnAndLineChart(data, item)}
            options={columnAndLineOption(showLegend, sign)}
            plugins={plugins}
          />
        )
      case 'doughnut':
        return (
          <Doughnut
            data={transformMultiGaugeChart(data, item)}
            options={doughnutOption(showLegend, sign)}
            plugins={plugins}
          />
        )
      case 'two_column':
        return (
          <Bar
            redraw
            data={transformTwoColumnChart(data, item)}
            options={twoColumnOption(showLegend, sign)}
            plugins={plugins}
          />
        )

      case 'gauge':
        return (
          <>
            <GaugeTargetChart
              options={guageOption(showLegend, sign)}
              data={transformMultiGaugeTargetChart(data, targetNumber)}
            />
            <Doughnut
              redraw
              data={transformMultiGaugeChart(data, item)}
              options={guageOption(showLegend, sign)}
              plugins={plugins}
            />
          </>
        )
      case 'table':
        return (
          <TableChart
            data={transformTableChart(data, item)}
            title="INSPECTION"
            periods={periods}
            id="INSPECTION"
          />
        )
      default:
        return <></>
      // return (
      //   <Chart
      //     type={'line'}
      //     options={options}
      //     data={transformMultiMultiChart(data, item)}
      //   />
      // )
    }
  }

  return (
    <MainWrapper
      id={id}
      chartTypes={menu}
      title={title}
      isTarget={target}
      onTarget={settargetNumber}
      onPeriodChange={onPeriodChange}
      periods={item?.show_period ? periods : []}
    >
      {renderChart(chartType)}
    </MainWrapper>
  )
}

export default MultiChart
